/**
 * Legal Document (legal board): navy header with every legal document as a tab, "On this page"
 * built from the document's sections, readable body (17px, 1.7 line height, ~68 characters),
 * and the cookie settings card. Loads markdown from /public/legal/.
 */

import { Logo } from '@/components/ui/Logo';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '@/hooks/useAuth';
import { cx } from '@/components/growth-desk/ui';

interface LegalDocumentProps {
  documentPath: string;
  title: string;
  description?: string;
}

export const LEGAL_DOCS: { to: string; label: string; also?: string[] }[] = [
  { to: '/privacy', label: 'Privacy', also: ['/privacy-policy'] },
  { to: '/terms', label: 'Terms', also: ['/terms-of-service'] },
  { to: '/cookie-policy', label: 'Cookies', also: ['/cookies'] },
  { to: '/sub-processors', label: 'Sub-processors' },
  { to: '/acceptable-use', label: 'Acceptable use', also: ['/acceptable-use-policy'] },
  { to: '/dpa', label: 'Data processing' },
  { to: '/data-deletion', label: 'Data deletion' },
];

const ACRONYMS = ['GDPR', 'DPDP', 'AI', 'API', 'EU', 'UK', 'USA', 'CCPA', 'DPO', 'SCC', 'SCCs', 'OAuth', 'IP', 'India', 'Meta', 'Google', 'Pinterest', 'TikTok', 'X'];
const textOf = (node: React.ReactNode): string =>
  typeof node === 'string' || typeof node === 'number' ? String(node)
    : Array.isArray(node) ? node.map(textOf).join('')
      : React.isValidElement(node) ? textOf((node.props as any).children) : '';
/** "SECTION 3: HOW WE USE INFORMATION {#pp-section-3}" → { id, label: "3 · How we use information" } */
const parseHeading = (raw: string, i: number) => {
  const id = raw.match(/\{#([\w-]+)\}/)?.[1] || `section-${i + 1}`;
  let t = raw.replace(/\{#[\w-]+\}/, '').trim();
  const m = t.match(/^(?:SECTION|Section|ARTICLE|Article|PART|Part)?\s*(\d+)[:.)]?\s*(.*)$/);
  const num = m?.[1];
  if (m) t = m[2];
  if (t === t.toUpperCase()) {
    t = t.toLowerCase().replace(/^\w/, c => c.toUpperCase());
    for (const a of ACRONYMS) t = t.replace(new RegExp(`\\b${a.toLowerCase()}\\b`, 'g'), a);
  }
  return { id, label: num ? `${num} · ${t}` : t };
};

/** The navy legal header: logo, sign in, title, meta line and every legal document as a tab. */
export const LegalHeader: React.FC<{ title: string; meta?: React.ReactNode }> = ({ title, meta }) => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const here = (d: typeof LEGAL_DOCS[number]) => pathname === d.to || !!d.also?.includes(pathname);
  return (
  <header className="bg-[#0F1526] text-white">
    <div className="mx-auto flex max-w-[1120px] flex-col gap-5 px-5 pb-8 pt-6 md:px-10">
      <div className="flex items-center justify-between gap-4">
        <Logo onDark height={30} />
        <Link to={user ? '/' : '/login'} className="text-sm font-semibold text-[#F5A266] underline underline-offset-2">{user ? 'Back to Admeru' : 'Sign in'}</Link>
      </div>
      <div className="flex flex-col gap-2 pt-4">
        <span className="text-label uppercase text-[#F5A266]">Legal</span>
        <h1 className="m-0 font-display text-[40px] font-extrabold leading-none tracking-[-0.04em] md:text-[60px]">{title}</h1>
        <p className="m-0 text-[15px] text-[#C9CEDB]">{meta}</p>
      </div>
      <nav aria-label="Legal documents" className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        {LEGAL_DOCS.map(d => (
          <Link key={d.to} to={d.to} aria-current={here(d) ? 'page' : undefined}
            className={cx('inline-flex min-h-[36px] shrink-0 items-center rounded-full px-3.5 text-sm font-bold no-underline transition-colors',
              here(d) ? 'bg-white text-[#0F1526]' : 'bg-[#1B2238] text-white hover:bg-[#262F4A]')}>
            {d.label}
          </Link>
        ))}
      </nav>
    </div>
  </header>
  );
};

export const LegalDocument: React.FC<LegalDocumentProps> = ({ documentPath, title }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetch(documentPath)
      .then(r => { if (!r.ok) throw new Error('Failed to load document'); return r.text(); })
      .then(text => { setContent(text); setError(null); })
      .catch(err => { console.error('Error loading legal document:', err); setError('Couldn’t load this document. Please try again later.'); })
      .finally(() => setLoading(false));
  }, [documentPath]);

  const lastUpdated = content.match(/\*\*Last Updated:\*\* (.*)/)?.[1];
  // The page has its own title, metadata line and contents list, so the document's H1, its
  // "Platform/Version/..." lines and its written table of contents are left out of the body.
  const body = useMemo(() => content
    .replace(/^# .*\n/, '')
    .replace(/^\*\*(Platform|Operated by|Version|Effective Date|Last Updated):\*\*.*\n/gm, '')
    .replace(/^## TABLE OF CONTENTS[\s\S]*?(?=^## )/m, ''), [content]);
  const sections = useMemo(() => (body.match(/^## .*$/gm) || []).map((h, i) => parseHeading(h.replace(/^## /, ''), i)), [body]);
  const minutes = Math.max(1, Math.round(body.split(/\s+/).length / 230));

  // Highlight the section in view.
  useEffect(() => {
    if (!sections.length) return;
    const els = sections.map(s => document.getElementById(s.id)).filter(Boolean) as HTMLElement[];
    const io = new IntersectionObserver(entries => {
      const top = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (top) setActive(top.target.id);
    }, { rootMargin: '0px 0px -70% 0px' });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [sections, body]);

  let h2Index = 0;

  return (
    <div className="min-h-[100dvh] bg-card text-ink">
      <LegalHeader title={title} meta={[lastUpdated && `Last updated ${lastUpdated}`, !loading && !error && `${minutes} min read`].filter(Boolean).join(' · ')} />

      <div className="mx-auto grid max-w-[1120px] gap-10 px-5 py-10 md:px-10 lg:grid-cols-[220px_1fr]">
        {sections.length > 0 && (
          <nav aria-label="On this page" className="hidden lg:block">
            <div className="sticky top-6 flex max-h-[calc(100dvh-48px)] flex-col gap-0.5 overflow-y-auto">
              <div className="px-3 pb-2 text-label uppercase text-ink-3">On this page</div>
              {sections.map(s => (
                <a key={s.id} href={`#${s.id}`}
                  className={cx('rounded-[10px] px-3 py-2 text-sm no-underline transition-colors', active === s.id ? 'bg-inset font-semibold text-ink' : 'text-ink-2 hover:text-ink')}>
                  {s.label}
                </a>
              ))}
            </div>
          </nav>
        )}

        <article className="min-w-0 max-w-[68ch] text-[17px] leading-[1.7] text-ink">
          {loading && <p className="text-ink-2">Loading…</p>}
          {error && (
            <div className="flex flex-col items-start gap-3 rounded-panel bg-ground p-6">
              <p className="m-0 font-display text-card-title font-bold">{error}</p>
              <button type="button" onClick={() => window.location.reload()} className="min-h-[44px] rounded-btn bg-[#0F1526] px-4 font-display text-sm font-bold text-white">Try again</button>
            </div>
          )}
          {!loading && !error && (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => <h2 className="mb-3 mt-10 font-display text-[28px] font-extrabold tracking-[-0.03em]">{children}</h2>,
                h2: ({ children }) => {
                  const s = parseHeading(textOf(children), h2Index++);
                  return <h2 id={s.id} className="mb-3 mt-12 scroll-mt-6 font-display text-[28px] font-extrabold leading-tight tracking-[-0.03em] first:mt-0">{s.label}</h2>;
                },
                h3: ({ children }) => <h3 className="mb-2 mt-8 font-display text-[20px] font-bold tracking-[-0.02em]">{textOf(children).replace(/\{#[\w-]+\}/, '')}</h3>,
                h4: ({ children }) => <h4 className="mb-2 mt-6 font-display text-[17px] font-bold">{children}</h4>,
                p: ({ children }) => <p className="mb-4 mt-0 text-ink">{children}</p>,
                ul: ({ children }) => <ul className="mb-4 ml-6 list-outside list-disc space-y-1.5">{children}</ul>,
                ol: ({ children }) => <ol className="mb-4 ml-6 list-outside list-decimal space-y-1.5">{children}</ol>,
                li: ({ children }) => <li>{children}</li>,
                a: ({ href, children }) => (
                  <a href={href} className="font-semibold text-brand-ink underline underline-offset-2"
                    target={href?.startsWith('http') ? '_blank' : undefined} rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}>{children}</a>
                ),
                code: ({ children }) => <code className="rounded bg-inset px-1.5 py-0.5 font-money text-[15px]">{children}</code>,
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                blockquote: ({ children }) => <blockquote className="mx-0 mb-6 mt-0 text-[19px] font-medium leading-relaxed text-ink [&_p]:mb-0 [&_strong]:font-bold">{children}</blockquote>,
                hr: () => <hr className="my-8 border-0 border-t border-line" />,
                table: ({ children }) => <div className="my-6 overflow-x-auto"><table className="w-full border-collapse text-[15px]">{children}</table></div>,
                thead: ({ children }) => <thead className="border-b-[1.5px] border-ink">{children}</thead>,
                tbody: ({ children }) => <tbody>{children}</tbody>,
                tr: ({ children }) => <tr className="border-b border-line">{children}</tr>,
                th: ({ children }) => <th className="px-3 py-2 text-left text-label uppercase text-ink-3">{children}</th>,
                td: ({ children }) => <td className="px-3 py-2 align-top">{children}</td>,
              }}
            >
              {body}
            </ReactMarkdown>
          )}

          <div className="mt-12 flex flex-wrap items-center justify-between gap-4 rounded-panel bg-ground px-6 py-5">
            <div>
              <div className="font-display text-card-title font-bold">Cookie settings</div>
              <div className="text-small text-ink-2">Needed cookies are always on. You choose the rest.</div>
            </div>
            <Link to="/cookie-settings" className="inline-flex min-h-[44px] items-center rounded-btn bg-navy px-4 font-display text-sm font-bold text-on-navy no-underline">Manage cookies</Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default LegalDocument;
